import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DevicesIcon from '@mui/icons-material/Devices';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Passkey from 'services/api/Passkey';
import getDeviceFriendlyName from 'utils/getDeviceFriendlyName';
import getDeviceIcon from 'utils/getDeviceIcon';
import { I18n } from 'utils/i18n';
import { isWebAuthnSupported } from 'utils/webAuthn';

const listItemSx = theme => ({
  mb: 1,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '& .MuiListItemText-root': {
    minHeight: 48,
    borderBottom: 0,
  },
});

const listItemContainerSx = theme => ({
  '& .MuiListItemText-root': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '&:last-child .MuiListItemText-root': {
    borderBottom: 0,
  },
});

const PasskeyManagementForm = ({ company = null, employee = null, isMe = false }) => {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [credentialToDelete, setCredentialToDelete] = useState(null);
  const [otpRequired, setOtpRequired] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const passkeySupported = useMemo(() => isWebAuthnSupported(), []);

  // Fetch existing passkey credentials
  const loadCredentials = useCallback(async () => {
    if (!company?._id) return;

    try {
      setLoading(true);
      const credentialsList = await Passkey.fetchCredentials(company._id, employee._id);
      setCredentials(credentialsList);
    } catch (error) {
      console.error('Error fetching passkey credentials:', error);
      setErrorMessage('Failed to load passkey information');
    } finally {
      setLoading(false);
    }
  }, [company, employee._id]);

  useEffect(() => {
    if (company?._id) {
      loadCredentials();
    }
  }, [company, loadCredentials]);

  const cancelOtp = () => {
    setOtpRequired(false);
    setOtpCode('');
    setErrorMessage(null);
  };

  // Register a new passkey
  const handleRegisterPasskey = async () => {
    if (registering || !company?._id || !employee?._id) return;

    try {
      setRegistering(true);
      setErrorMessage(null);

      await Passkey.registerPasskey({
        companyId: company._id,
        userId: employee._id,
        username: employee.email || `${employee.firstName} ${employee.surName}`,
      });

      setOtpRequired(false);
      setOtpCode('');
      // Refresh credentials list
      loadCredentials();
    } catch (error) {
      if (error?.code === 'otpRequired') {
        try {
          await Passkey.sendRegistrationOtp(company._id);
          setOtpRequired(true);
          setErrorMessage(null);
        } catch (sendError) {
          console.error('Error sending registration OTP:', sendError);
          setErrorMessage(
            sendError.message ||
              I18n.t('SettingsDrawer.Passkey_OTP_Send_Error', 'Failed to send verification code'),
          );
        }
      } else {
        console.error('Error registering passkey:', error);
        setErrorMessage(
          error.message ||
            I18n.t('SettingsDrawer.Passkey_Register_Error', 'Failed to register passkey'),
        );
      }
    } finally {
      setRegistering(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (verifyingOtp || !company?._id || !otpCode.trim()) return;

    try {
      setVerifyingOtp(true);
      setErrorMessage(null);
      await Passkey.verifyRegistrationOtp(company._id, otpCode.trim());
      setOtpRequired(false);
      setOtpCode('');
      await handleRegisterPasskey();
    } catch (error) {
      console.error('Error verifying registration OTP:', error);
      setErrorMessage(
        error.message || I18n.t('SettingsDrawer.Passkey_OTP_Verify_Error', 'Failed to verify code'),
      );
    } finally {
      setVerifyingOtp(false);
    }
  };

  // Open delete confirmation dialog
  const openDeleteDialog = credentialId => {
    setCredentialToDelete(credentialId);
    setDeleteDialogOpen(true);
  };

  // Close delete confirmation dialog
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCredentialToDelete(null);
  };

  // Confirm and delete a passkey credential
  const confirmDeleteCredential = async () => {
    if (!company?._id || !credentialToDelete) return;

    try {
      await Passkey.deleteCredential(company._id, credentialToDelete);
      // Update the local state
      setCredentials(credentials.filter(cred => cred.id !== credentialToDelete));
      closeDeleteDialog();
    } catch (error) {
      console.error('Error deleting passkey:', error);
      setErrorMessage('Failed to delete passkey');
      closeDeleteDialog();
    }
  };

  const renderOtpForm = () => (
    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body1">
        {I18n.t(
          'SettingsDrawer.Passkey_OTP_Prompt',
          'Enter the SMS code we sent to verify your phone before adding a passkey',
        )}
      </Typography>
      <TextField
        label={I18n.t('SettingsDrawer.Passkey_OTP_Code', 'SMS code')}
        value={otpCode}
        onChange={event => setOtpCode(event.target.value)}
        fullWidth
        autoFocus
        slotProps={{ htmlInput: { inputMode: 'numeric', autoComplete: 'one-time-code' } }}
      />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="contained"
          color="primary"
          disabled={verifyingOtp || registering || !otpCode.trim()}
          startIcon={verifyingOtp || registering ? <CircularProgress size={20} /> : null}
          onClick={handleVerifyOtp}
        >
          {I18n.t('SettingsDrawer.Passkey_OTP_Verify', 'Verify and continue')}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          disabled={verifyingOtp || registering}
          onClick={cancelOtp}
        >
          {I18n.t('Cancel', 'Cancel')}
        </Button>
      </Box>
    </Box>
  );

  // Render passkey content based on support and loading status
  const renderPasskeyContent = () => {
    // If WebAuthn is not supported
    if (!passkeySupported) {
      return (
        <Box
          sx={theme => ({
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
            borderRadius: theme.shape.borderRadius,
            backgroundColor: theme.palette.background.paper,
            border: `1px dashed ${theme.palette.divider}`,
          })}
        >
          <Box sx={{ mb: 2 }}>
            <WarningIcon sx={{ color: 'warning.main', fontSize: 48 }} />
          </Box>
          <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
            {I18n.t(
              'SettingsDrawer.Passkey_Not_Available',
              'Passkeys are not supported in this browser',
            )}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {I18n.t(
              'SettingsDrawer.Try_Another_Browser',
              'Please try using a modern browser like Chrome, Safari, or Firefox',
            )}
          </Typography>
        </Box>
      );
    }

    // Loading state
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (otpRequired) {
      return renderOtpForm();
    }

    // No credentials state
    if (credentials.length === 0) {
      return (
        <Box
          sx={theme => ({
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
            borderRadius: theme.shape.borderRadius,
            backgroundColor: theme.palette.background.paper,
            border: `1px dashed ${theme.palette.divider}`,
          })}
        >
          <Box sx={{ mb: 2 }}>
            <DevicesIcon sx={{ color: 'primary.main', fontSize: 48 }} />
          </Box>
          <Typography variant="body1" gutterBottom>
            {I18n.t('SettingsDrawer.No_Passkeys', 'No passkeys registered')}
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {I18n.t('SettingsDrawer.Passkey_Description')}
          </Typography>
          {!isMe || (
            <Button
              variant="contained"
              color="primary"
              disabled={registering}
              startIcon={registering ? <CircularProgress size={20} /> : <AddIcon />}
              onClick={handleRegisterPasskey}
              sx={{ mt: 2 }}
            >
              {I18n.t('SettingsDrawer.Add_Passkey', 'Add a passkey')}
            </Button>
          )}
        </Box>
      );
    }

    // Credentials list
    return (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
          {!isMe || (
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={registering}
              startIcon={registering ? <CircularProgress size={16} /> : <AddIcon />}
              onClick={handleRegisterPasskey}
            >
              {I18n.t('SettingsDrawer.Add_Passkey', 'Add')}
            </Button>
          )}
        </Box>

        <Box
          sx={theme => ({
            overflowY: 'auto',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
          })}
        >
          <List disablePadding>
            {credentials.map(credential => {
              const createdDate = credential.createdAt
                ? moment.unix(credential.createdAt).format('LLL')
                : '';
              const deviceFriendlyName = getDeviceFriendlyName(credential.os, credential.name);

              return (
                <ListItem key={credential.id} sx={[listItemContainerSx, listItemSx]}>
                  <Box sx={{ mr: 2 }}>
                    {getDeviceIcon(credential.os, credential.name, deviceFriendlyName)}
                  </Box>
                  <ListItemText
                    primary={deviceFriendlyName}
                    secondary={
                      createdDate && (
                        <Typography variant="body2" sx={{ fontSize: 14, color: 'text.secondary' }}>
                          {I18n.t(
                            'SettingsDrawer.Created_On',
                            { date: createdDate },
                            'Created on {date}',
                          )}
                        </Typography>
                      )
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title={I18n.t('SettingsDrawer.Delete_Passkey', 'Delete passkey')}>
                      <IconButton
                        edge="end"
                        onClick={() => openDeleteDialog(credential.id)}
                        size="large"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        </Box>
      </>
    );
  };

  return (
    <Box
      component="form"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 455,
        padding: '20px 45px 45px 40px',
        height: '100vh',
        overflowY: 'hidden',
      }}
    >
      <Typography
        sx={{
          fontSize: 28,
          fontWeight: 'bold',
          fontStyle: 'normal',
          fontStretch: 'normal',
          lineHeight: '38px',
        }}
      >
        {I18n.t('SettingsDrawer.Passkey_Management', 'Passkey Management')}
      </Typography>
      <Box
        sx={{ marginTop: '40px', display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}
      >
        {errorMessage && (
          <Typography color="error" variant="body2" gutterBottom>
            {errorMessage}
          </Typography>
        )}
        {renderPasskeyContent()}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {I18n.t('SettingsDrawer.Delete_Passkey_Title', 'Delete Passkey')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {I18n.t(
              'SettingsDrawer.Delete_Passkey_Confirmation',
              'Are you sure you want to delete this passkey? You will no longer be able to use it to sign in to your account.',
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary">
            {I18n.t('Cancel', 'Cancel')}
          </Button>
          <Button onClick={confirmDeleteCredential} color="error" autoFocus>
            {I18n.t('Delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

PasskeyManagementForm.propTypes = {
  company: PropTypes.shape({
    _id: PropTypes.string,
  }),
  employee: PropTypes.shape({
    _id: PropTypes.string,
    email: PropTypes.string,
    firstName: PropTypes.string,
    surName: PropTypes.string,
  }),
  isMe: PropTypes.bool,
};

export default PasskeyManagementForm;
